import { Injector, inject, runInInjectionContext } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  ResolveFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

export const resolveData = <T>(
  resolveFn: (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ) => Promise<T>,
): ResolveFn<T | undefined> => {
  return async (route, state) => {
    const injector = inject(Injector);
    const router = inject(Router);
    const resolved = await runInInjectionContext(injector, () =>
      resolveFn(route, state),
    );

    if (!resolved) {
      router.navigate(['/not-found']);
      return undefined;
    }

    return resolved;
  };
};

export type ResolveDataWithParamFn<T> = (
  param: any,
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => Promise<T>;

export function resolveDataWithParam<T, Param extends string>(
  param: Param,
  resolveFn: ResolveDataWithParamFn<T>,
): ResolveFn<T | undefined>;
export function resolveDataWithParam<T>(
  resolveFn: ResolveDataWithParamFn<T>,
): ResolveFn<T | undefined>;
export function resolveDataWithParam<T, Param extends string = 'id'>(
  resolveFnOrParamKey: ResolveDataWithParamFn<T> | Param,
  resolveFn?: ResolveDataWithParamFn<T>,
): ResolveFn<T | undefined> {
  return async (route, state) => {
    const injector = inject(Injector);
    const router = inject(Router);
    const paramKey =
      typeof resolveFnOrParamKey === 'string' ? resolveFnOrParamKey : 'id';
    const param =
      route.params[paramKey] ||
      route.parent?.params[paramKey] ||
      route.parent?.parent?.params[paramKey];

    const resolved = await runInInjectionContext(injector, () =>
      (resolveFn || (resolveFnOrParamKey as ResolveDataWithParamFn<T>))(
        param,
        route,
        state,
      ),
    );

    if (!resolved) {
      router.navigate(['/not-found']);
      return undefined;
    }

    return resolved;
  };
}
